import React, { useEffect, useState } from "react";
import {
  Box, Card, CardContent, Typography, TextField, Select, MenuItem,
  FormControl, InputLabel, Button, Divider, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, IconButton,
  Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Skeleton, LinearProgress
} from "@mui/material";
import {
  Send, Add, Delete, Campaign, People, AttachMoney, Work,
  Notifications, LocalOffer, BarChart, CheckCircle
} from "@mui/icons-material";
import {
  collection, onSnapshot, addDoc, deleteDoc, doc,
  query, orderBy, getDocs, Timestamp, where
} from "firebase/firestore";
import { db } from "../firebase";
import { getFunctions, httpsCallable } from "firebase/functions";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PromoCode {
  id: string;
  code: string;
  discountPercent: number;
  expiryDate: string;
  isActive: boolean;
  usageCount: number;
  createdAt: any;
}

interface AppStats {
  totalUsers: number;
  totalJobs: number;
  completedJobs: number;
  totalRevenue: number;
  loading: boolean;
}

// ─── App Stats Card ────────────────────────────────────────────────────────────

function AppStatsSection({ stats }: { stats: AppStats }) {
  const statItems = [
    { label: "Total Users", value: stats.loading ? "…" : stats.totalUsers, color: "#6366f1", icon: <People /> },
    { label: "Total Jobs", value: stats.loading ? "…" : stats.totalJobs, color: "#3b82f6", icon: <Work /> },
    { label: "Completed Jobs", value: stats.loading ? "…" : stats.completedJobs, color: "#22c55e", icon: <CheckCircle /> },
    {
      label: "Total Revenue",
      value: stats.loading ? "…" : `$${stats.totalRevenue.toFixed(0)}`,
      color: "#f59e0b",
      icon: <AttachMoney />
    },
  ];

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 2 }}>
      {statItems.map((item) => (
        <Card key={item.label}>
          <CardContent>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing={0.5}>
                  {item.label}
                </Typography>
                {stats.loading ? (
                  <Skeleton width={80} height={40} />
                ) : (
                  <Typography variant="h4" fontWeight={800} sx={{ mt: 0.5 }}>{item.value}</Typography>
                )}
              </Box>
              <Box sx={{ bgcolor: `${item.color}22`, borderRadius: 2, p: 1.5, color: item.color }}>
                {item.icon}
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
}

// ─── Push Notification Panel ───────────────────────────────────────────────────

function PushNotificationPanel() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [target, setTarget] = useState("all_customers");
  const [specificUserId, setSpecificUserId] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ type: "success" | "error"; message: string } | null>(null);

  const handleSend = async () => {
    if (!title.trim() || !body.trim()) {
      setResult({ type: "error", message: "Title and body are required." });
      return;
    }
    if (target === "specific_user" && !specificUserId.trim()) {
      setResult({ type: "error", message: "Please enter a user ID." });
      return;
    }

    setSending(true);
    setResult(null);

    try {
      const functions = getFunctions();
      const sendPush = httpsCallable(functions, "sendAdminPushNotification");
      await sendPush({
        title: title.trim(),
        body: body.trim(),
        target,
        userId: target === "specific_user" ? specificUserId.trim() : undefined,
      });
      setResult({ type: "success", message: `Push notification sent to: ${target.replace(/_/g, " ")}` });
      setTitle("");
      setBody("");
      setSpecificUserId("");
    } catch (err: any) {
      // If function doesn't exist yet, show friendly error
      setResult({
        type: "error",
        message: err.message?.includes("NOT_FOUND")
          ? "sendAdminPushNotification function not yet deployed. Deploy the function to enable this."
          : `Failed: ${err.message}`
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2.5 }}>
          <Box sx={{ bgcolor: "#6366f122", borderRadius: 2, p: 1, color: "#6366f1" }}>
            <Notifications />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700}>Send Push Notification</Typography>
            <Typography variant="caption" color="text.secondary">Broadcast to customers, techs, or a specific user</Typography>
          </Box>
        </Box>

        {result && (
          <Alert severity={result.type} sx={{ mb: 2 }} onClose={() => setResult(null)}>
            {result.message}
          </Alert>
        )}

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="Notification Title"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. Special offer just for you!"
            fullWidth
            inputProps={{ maxLength: 80 }}
            helperText={`${title.length}/80`}
          />
          <TextField
            label="Notification Body"
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="e.g. Book a tech today and save 20% with code SAVE20"
            fullWidth
            multiline
            rows={3}
            inputProps={{ maxLength: 200 }}
            helperText={`${body.length}/200`}
          />
          <Box sx={{ display: "flex", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Target Audience</InputLabel>
              <Select
                value={target}
                label="Target Audience"
                onChange={e => setTarget(e.target.value)}
              >
                <MenuItem value="all_customers">All Customers</MenuItem>
                <MenuItem value="all_techs">All Technicians</MenuItem>
                <MenuItem value="tech_pass">Tech Pass Subscribers</MenuItem>
                <MenuItem value="specific_user">Specific User</MenuItem>
              </Select>
            </FormControl>
            {target === "specific_user" && (
              <TextField
                label="User ID or FCM Token"
                value={specificUserId}
                onChange={e => setSpecificUserId(e.target.value)}
                fullWidth
                placeholder="Firebase UID or FCM token"
              />
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <Send />}
            onClick={handleSend}
            disabled={sending || !title || !body}
            sx={{ alignSelf: "flex-start", px: 3 }}
          >
            {sending ? "Sending…" : "Send Notification"}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

// ─── Promo Codes Panel ────────────────────────────────────────────────────────

function PromoCodesPanel() {
  const [codes, setCodes] = useState<PromoCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PromoCode | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [newCode, setNewCode] = useState({ code: "", discountPercent: 10, expiryDate: "" });
  const [creating, setCreating] = useState(false);
  const [alert, setAlert] = useState<{ type: "success" | "error"; msg: string } | null>(null);

  useEffect(() => {
    const q = query(collection(db, "promoCodes"), orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, (snap) => {
      setCodes(snap.docs.map(d => ({ id: d.id, ...d.data() } as PromoCode)));
      setLoading(false);
    });
    return unsub;
  }, []);

  const handleCreate = async () => {
    if (!newCode.code.trim() || !newCode.expiryDate) {
      setAlert({ type: "error", msg: "Code and expiry date are required." });
      return;
    }
    setCreating(true);
    try {
      await addDoc(collection(db, "promoCodes"), {
        code: newCode.code.toUpperCase().trim(),
        discountPercent: Number(newCode.discountPercent),
        expiryDate: newCode.expiryDate,
        isActive: true,
        usageCount: 0,
        createdAt: Timestamp.now(),
      });
      setAlert({ type: "success", msg: `Promo code ${newCode.code.toUpperCase()} created!` });
      setNewCode({ code: "", discountPercent: 10, expiryDate: "" });
      setCreateOpen(false);
    } catch (e: any) {
      setAlert({ type: "error", msg: `Failed: ${e.message}` });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteDoc(doc(db, "promoCodes", deleteTarget.id));
    setAlert({ type: "success", msg: `Promo code ${deleteTarget.code} deleted.` });
    setDeleteOpen(false);
    setDeleteTarget(null);
  };

  const isExpired = (expiryDate: string) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box sx={{ bgcolor: "#f59e0b22", borderRadius: 2, p: 1, color: "#f59e0b" }}>
              <LocalOffer />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>Promo Codes</Typography>
              <Typography variant="caption" color="text.secondary">Create and manage discount codes</Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Add />}
            size="small"
            onClick={() => setCreateOpen(true)}
          >
            New Code
          </Button>
        </Box>

        {alert && (
          <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
            {alert.msg}
          </Alert>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><Typography variant="caption" fontWeight={700} textTransform="uppercase">Code</Typography></TableCell>
                <TableCell align="center"><Typography variant="caption" fontWeight={700} textTransform="uppercase">Discount</Typography></TableCell>
                <TableCell align="center"><Typography variant="caption" fontWeight={700} textTransform="uppercase">Uses</Typography></TableCell>
                <TableCell><Typography variant="caption" fontWeight={700} textTransform="uppercase">Expires</Typography></TableCell>
                <TableCell align="center"><Typography variant="caption" fontWeight={700} textTransform="uppercase">Status</Typography></TableCell>
                <TableCell align="right"><Typography variant="caption" fontWeight={700} textTransform="uppercase">Actions</Typography></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((_, j) => (
                      <TableCell key={j}><Skeleton /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : codes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                    <Typography color="text.secondary">No promo codes yet. Create your first one!</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                codes.map((code) => {
                  const expired = isExpired(code.expiryDate);
                  return (
                    <TableRow key={code.id} sx={{ opacity: expired ? 0.5 : 1 }}>
                      <TableCell>
                        <Typography fontWeight={700} fontSize={14} sx={{ fontFamily: "monospace", letterSpacing: 1 }}>
                          {code.code}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${code.discountPercent}% OFF`}
                          size="small"
                          color="warning"
                          sx={{ fontWeight: 700 }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography fontSize={13}>{code.usageCount ?? 0}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography fontSize={13} color={expired ? "error" : "text.secondary"}>
                          {code.expiryDate || "No expiry"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={expired ? "Expired" : code.isActive ? "Active" : "Inactive"}
                          size="small"
                          color={expired ? "default" : code.isActive ? "success" : "error"}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => { setDeleteTarget(code); setDeleteOpen(true); }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Create Promo Code</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField
            label="Promo Code"
            value={newCode.code}
            onChange={e => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })}
            placeholder="e.g. SAVE20"
            fullWidth
            inputProps={{ style: { fontFamily: "monospace", letterSpacing: 2 } }}
          />
          <TextField
            label="Discount %"
            type="number"
            value={newCode.discountPercent}
            onChange={e => setNewCode({ ...newCode, discountPercent: Number(e.target.value) })}
            fullWidth
            inputProps={{ min: 1, max: 100 }}
          />
          <TextField
            label="Expiry Date"
            type="date"
            value={newCode.expiryDate}
            onChange={e => setNewCode({ ...newCode, expiryDate: e.target.value })}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} variant="outlined">Cancel</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={creating}
            startIcon={creating ? <CircularProgress size={14} color="inherit" /> : <Add />}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs">
        <DialogTitle>Delete Promo Code</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleteTarget?.code}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} variant="outlined">Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained" startIcon={<Delete />}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

// ─── Main View ────────────────────────────────────────────────────────────────

export function MarketingView() {
  const [stats, setStats] = useState<AppStats>({
    totalUsers: 0,
    totalJobs: 0,
    completedJobs: 0,
    totalRevenue: 0,
    loading: true,
  });

  useEffect(() => {
    // Load app-wide stats
    Promise.all([
      getDocs(collection(db, "users")),
      getDocs(collection(db, "jobs")),
    ]).then(([usersSnap, jobsSnap]) => {
      const jobs = jobsSnap.docs.map(d => d.data());
      const completedJobs = jobs.filter(j => j.status === "completed");
      const totalRevenue = completedJobs.reduce((sum, j) => {
        const pricing = j.pricing as any;
        return sum + (pricing?.finalTotal ?? pricing?.estimatedTotal ?? 0);
      }, 0);
      setStats({
        totalUsers: usersSnap.size,
        totalJobs: jobsSnap.size,
        completedJobs: completedJobs.length,
        totalRevenue,
        loading: false,
      });
    });
  }, []);

  return (
    <Box>
      <Typography variant="h5" fontWeight={800} sx={{ mb: 3 }}>Marketing & Outreach</Typography>

      {/* App Stats */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
          <BarChart sx={{ color: "primary.main" }} /> App Stats
        </Typography>
        <AppStatsSection stats={stats} />
      </Box>

      {/* Push Notifications */}
      <Box sx={{ mb: 4 }}>
        <PushNotificationPanel />
      </Box>

      {/* Promo Codes */}
      <Box sx={{ mb: 4 }}>
        <PromoCodesPanel />
      </Box>
    </Box>
  );
}
